/**
 * Brand Configuration Utilities
 * Functions for resolving brand configuration and converting to DesignBaseSettings format
 */

import { isSupportedLanguage } from '@ophelos/shared/utils/languages';
import { configEntityId } from '@/lib/debt-type-cohort';
import { deepMerge, THEME_DEFAULTS } from '@/lib/schema-utils';
import type { BrandConfiguration } from '@/lib/types/brand-config';
import { DEFAULT_BRAND_CONFIG } from '@/lib/types/brand-config';
import { getCompiledConfig } from '@/services/config';
import {
  type Client,
  DEFAULT_DESIGN_BASE_SETTINGS,
  type DebtType,
  type DesignBaseSettings,
  type DesignColors,
  Domain,
  type Operator,
} from '@/types';
import type { ContentConfig } from '@/types/config/content';
import type { JourneyConfig } from '@/types/config/journey';
import type { ThemeConfig } from '@/types/config/theme';

/**
 * Email design colors - all values sourced from THEME_DEFAULTS
 */
export const EMAIL_DESIGN_DEFAULTS: Pick<
  DesignColors,
  'accent' | 'muted' | 'text' | 'border' | 'background' | 'links' | 'buttonBackground' | 'buttonText'
> = {
  accent: THEME_DEFAULTS.colors.accent,
  muted: THEME_DEFAULTS.colors.muted,
  border: THEME_DEFAULTS.colors.border,
  text: THEME_DEFAULTS.colors.text,
  background: THEME_DEFAULTS.colors.emailBackground.light,
  links: THEME_DEFAULTS.colors.linksColor.light,
  buttonBackground: {
    light: THEME_DEFAULTS.colors.buttonBackground.light,
    dark: THEME_DEFAULTS.colors.buttonBackground.dark,
  },
  buttonText: {
    light: THEME_DEFAULTS.colors.buttonText.light,
    dark: THEME_DEFAULTS.colors.buttonText.dark,
  },
};

/**
 * Convert ThemeConfig to brand configuration theme fields
 */
export function themeConfigToBrand(theme: ThemeConfig | null): Partial<BrandConfiguration> {
  if (!theme) return {};

  return {
    theme: theme.theme as 'professional' | 'approachable' | undefined,
    showGradientSphere: theme.showGradientSphere,
    defaultLanguage: theme.defaultLanguage,
    availableLanguages: theme.availableLanguages,
    uiFeatures: theme.features
      ? {
          enableChat: theme.features.enableChat ?? false,
          showDisputes: theme.features.showDisputes ?? true,
          showDocuments: theme.features.showDocuments ?? true,
          showAccountBalance: theme.features.showAccountBalance ?? true,
          showPaymentHistory: theme.features.showPaymentHistory ?? true,
        }
      : undefined,
    logo_full: theme.logo_full,
    logo_icon: theme.logo_icon,
    favicon: theme.favicon,
    colors: {
      brand: theme.colors?.brand || '#7f53dd',
      buttonBackground: theme.colors?.buttonBackground,
      buttonText: theme.colors?.buttonText,
      linksColor: theme.colors?.linksColor,
      emailBackground: theme.colors?.emailBackground,
      text: theme.colors?.text,
      muted: theme.colors?.muted,
      border: theme.colors?.border,
      accent: theme.colors?.accent,
    },
    layout: theme.layout ? { borderRadius: theme.layout.borderRadius || 'medium' } : null,
  };
}

/**
 * Convert ContentConfig to brand configuration content fields
 */
export function contentConfigToBrand(content: ContentConfig | null): Partial<BrandConfiguration> {
  if (!content) return {};

  return {
    company_name: content.company_info?.company_name || null,
    company_phone: content.company_info?.company_phone || null,
    company_email: content.company_info?.company_email || null,
    company_address: content.company_info?.company_address || null,
    support_url: content.company_info?.support_url || null,
    support_email: content.company_info?.support_email || null,
    support_phone: content.company_info?.support_phone || null,
    support_address: content.company_info?.support_address || null,
    return_address: content.company_info?.return_address || null,
    privacy_policy_url: content.company_info?.privacy_policy_url || null,
    terms_url: content.company_info?.terms_url || null,
    unsubscribe_url_template: content.company_info?.unsubscribe_url_template || null,
    // Preserve localized names - keep as object if localized, convert to string only if single locale
    journey_footer_links: (content.footer_links || []).map((link) => ({
      name: typeof link.name === 'string' ? link.name : link.name, // Keep localized object as-is
      url: link.url,
    })),
  };
}

/**
 * Convert JourneyConfig to brand configuration journey fields
 */
export function journeyConfigToBrand(journey: JourneyConfig | null): Partial<BrandConfiguration> {
  if (!journey) return {};

  return {
    payment: journey.payment_settings
      ? {
          allowPartialPayment: journey.payment_settings.allowPartialPayment ?? true,
          enablePaymentPlan: journey.payment_settings.enablePaymentPlan ?? true,
        }
      : undefined,
    paymentPlanTiers: journey.payment_plan_tiers,
    // uiFeatures and localization removed from journey schema - using defaults
    uiFeatures: undefined,
    defaultLanguage: undefined,
    availableLanguages: undefined,
  };
}

/**
 * Resolve brand configuration for a given scope
 * Uses compiled_config_for which handles hierarchical merging automatically
 * Returns hierarchical brand config (system → operator → debtType/client)
 */
export async function resolveBrandConfig(
  operator: Operator | null,
  debtType: DebtType | null,
  client: Client | null
): Promise<BrandConfiguration | null> {
  if (!operator) {
    return null;
  }

  // Start with default config
  let merged: BrandConfiguration = { ...DEFAULT_BRAND_CONFIG };

  // Determine most specific entity ID (client > debtType > operator)
  const entityId = configEntityId(debtType, client, operator.id);

  try {
    // Fetch compiled configs for each domain (already hierarchically merged by DB)
    const [themeConfig, contentConfig, journeyConfig] = await Promise.all([
      getCompiledConfig(entityId, Domain.THEME),
      getCompiledConfig(entityId, Domain.CONTENT),
      getCompiledConfig(entityId, Domain.JOURNEY),
    ]);

    // Merge compiled configs into brand config
    if (themeConfig?.config_json) {
      merged = deepMerge(merged, themeConfigToBrand(themeConfig.config_json as ThemeConfig));
    }
    if (contentConfig?.config_json) {
      merged = deepMerge(merged, contentConfigToBrand(contentConfig.config_json as ContentConfig));
    }
    if (journeyConfig?.config_json) {
      merged = deepMerge(merged, journeyConfigToBrand(journeyConfig.config_json as JourneyConfig));
    }

    console.log('[resolveBrandConfig] Resolved config for operator:', operator.name);
  } catch (error) {
    console.error('[resolveBrandConfig] Error loading brand config from database:', error);
    // Return defaults on error
    return merged;
  }

  return merged;
}

/**
 * Convert BrandConfiguration to DesignBaseSettings format
 * Used for compatibility with existing design editor components
 * Returns base settings (colors, layout, variables) without blocks/blockConfigs
 */
export function brandConfigToDesignBaseSettings(brandConfig: BrandConfiguration | null): DesignBaseSettings {
  if (!brandConfig) {
    return DEFAULT_DESIGN_BASE_SETTINGS;
  }

  return {
    colors: {
      primary: brandConfig.colors.brand, // Map brand to primary for email design system
      background: brandConfig.colors.emailBackground?.light || EMAIL_DESIGN_DEFAULTS.background,
      muted: brandConfig.colors.muted || EMAIL_DESIGN_DEFAULTS.muted,
      accent: brandConfig.colors.accent || EMAIL_DESIGN_DEFAULTS.accent,
      text: brandConfig.colors.text || EMAIL_DESIGN_DEFAULTS.text,
      links: brandConfig.colors.linksColor?.light || brandConfig.colors.brand || EMAIL_DESIGN_DEFAULTS.links,
      border: brandConfig.colors.border || EMAIL_DESIGN_DEFAULTS.border,
      buttonBackground: {
        // Only use light mode values for emails (emails only support light mode)
        light: brandConfig.colors.buttonBackground?.light || brandConfig.colors.brand || EMAIL_DESIGN_DEFAULTS.buttonBackground.light,
        dark: brandConfig.colors.buttonBackground?.light || brandConfig.colors.brand || EMAIL_DESIGN_DEFAULTS.buttonBackground.light, // Use light value for dark too
      },
      buttonText: {
        // Only use light mode values for emails (emails only support light mode)
        light: brandConfig.colors.buttonText?.light || EMAIL_DESIGN_DEFAULTS.buttonText.light,
        dark: brandConfig.colors.buttonText?.light || EMAIL_DESIGN_DEFAULTS.buttonText.light, // Use light value for dark too
      },
    },
    layout: {
      borderRadius: brandConfig.layout?.borderRadius || 'medium',
    },
    variables: {
      company_name: brandConfig.company_name || null,
      company_phone: brandConfig.company_phone || null,
      company_email: brandConfig.company_email || null,
      company_address: brandConfig.company_address || null,
      logo_url: brandConfig.logo_full?.light || brandConfig.logo_icon?.light || null, // Deprecated: kept for backward compatibility
      logo_full_url_light: brandConfig.logo_full?.light || null,
      logo_full_url_dark: brandConfig.logo_full?.dark || null,
      logo_icon_url_light: brandConfig.logo_icon?.light || null,
      logo_icon_url_dark: brandConfig.logo_icon?.dark || null,
      support_url: brandConfig.support_url || null,
      support_email: brandConfig.support_email || null,
      support_phone: brandConfig.support_phone || null,
      support_address: brandConfig.support_address || null,
      privacy_policy_url: brandConfig.privacy_policy_url || null,
      terms_url: brandConfig.terms_url || null,
      unsubscribe_url_template: brandConfig.unsubscribe_url_template || null,
    },
  };
}

type ThemeColors = NonNullable<ThemeConfig['colors']>;

/**
 * Keep only the modes that carry a value, so a logo configured for one mode
 * still round-trips. Returns undefined when no mode is set, so an empty object
 * is never persisted as a scope override.
 */
function definedModes<T extends Record<string, string | undefined>>(pair: T | undefined): Partial<T> | undefined {
  if (!pair) return undefined;
  const modes = Object.fromEntries(Object.entries(pair).filter(([, url]) => url)) as Partial<T>;
  return Object.keys(modes).length > 0 ? modes : undefined;
}

/**
 * Convert brand configuration form state back to the theme domain config that is saved.
 * Inverse of {@link themeConfigToBrand}.
 */
export function brandConfigToThemeConfig(brandConfig: BrandConfiguration): ThemeConfig {
  const themeConfig: ThemeConfig = {};

  if (brandConfig.theme === 'professional' || brandConfig.theme === 'approachable') {
    themeConfig.theme = brandConfig.theme;
  }

  if (brandConfig.showGradientSphere !== undefined) {
    themeConfig.showGradientSphere = brandConfig.showGradientSphere;
  }

  if (brandConfig.defaultLanguage && isSupportedLanguage(brandConfig.defaultLanguage)) {
    themeConfig.defaultLanguage = brandConfig.defaultLanguage;
  }

  if (brandConfig.availableLanguages && brandConfig.availableLanguages.length > 0) {
    const filteredLanguages = brandConfig.availableLanguages.filter((lang) => isSupportedLanguage(lang));
    if (filteredLanguages.length > 0) {
      themeConfig.availableLanguages = filteredLanguages;
    }
  }

  if (brandConfig.uiFeatures) {
    themeConfig.features = {
      enableChat: brandConfig.uiFeatures.enableChat,
      showDisputes: brandConfig.uiFeatures.showDisputes,
      showDocuments: brandConfig.uiFeatures.showDocuments,
      showAccountBalance: brandConfig.uiFeatures.showAccountBalance,
      showPaymentHistory: brandConfig.uiFeatures.showPaymentHistory,
    };
  }

  const logoIcon = definedModes(brandConfig.logo_icon);
  if (logoIcon) themeConfig.logo_icon = logoIcon as ThemeConfig['logo_icon'];

  const logoFull = definedModes(brandConfig.logo_full);
  if (logoFull) themeConfig.logo_full = logoFull as ThemeConfig['logo_full'];

  const favicon = definedModes(brandConfig.favicon);
  if (favicon) themeConfig.favicon = favicon as ThemeConfig['favicon'];

  if (brandConfig.colors) {
    themeConfig.colors = {
      brand: brandConfig.colors.brand,
    };

    const buttonBackground = definedModes(brandConfig.colors.buttonBackground);
    if (buttonBackground) themeConfig.colors.buttonBackground = buttonBackground as ThemeColors['buttonBackground'];

    const buttonText = definedModes(brandConfig.colors.buttonText);
    if (buttonText) themeConfig.colors.buttonText = buttonText as ThemeColors['buttonText'];

    if (brandConfig.colors.linksColor?.light) {
      themeConfig.colors.linksColor = { light: brandConfig.colors.linksColor.light };
    }

    if (brandConfig.colors.emailBackground?.light) {
      themeConfig.colors.emailBackground = { light: brandConfig.colors.emailBackground.light };
    }

    if (brandConfig.colors.text) themeConfig.colors.text = brandConfig.colors.text;
    if (brandConfig.colors.muted) themeConfig.colors.muted = brandConfig.colors.muted;
    if (brandConfig.colors.border) themeConfig.colors.border = brandConfig.colors.border;
    if (brandConfig.colors.accent) themeConfig.colors.accent = brandConfig.colors.accent;
  }

  if (brandConfig.layout?.borderRadius) {
    themeConfig.layout = {
      borderRadius: brandConfig.layout.borderRadius,
    };
  }

  return themeConfig;
}
